import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { Breadcrumb } from "@app/models/breadcrumb.model";
import { ZendeskQAToken } from "@app/models/integrations/zendesk-qa/zendesk-qa-token.model";
import { IState } from "@app/models/state/state.model";
import { ZendeskQAAPIService } from "@app/shared/api/zendesk-qa.api.service";
import { BreadcrumbService } from "@app/shared/breadcrumbs/breadcrumbs.service";
import { ButtonType } from "@app/shared/components/inputs/button/button.component";
import { PillType } from "@app/shared/components/pill/pill.component";
import { NotifyUtils } from "@app/shared/utils/notify.utils";
import { SwalUtils } from "@app/shared/utils/swal.utils";

interface PageState extends IState {
  isConfiguring: boolean;

  submitted: boolean;
  submitting: boolean;
}

@Component({
  selector: "app-integration-zendesk-qa",
  templateUrl: "./integration-zendesk-qa.component.html",
  styleUrls: ["./integration-zendesk-qa.component.scss"],
})
export class IntegrationZendeskQaComponent implements OnInit, OnDestroy {
  public readonly eButtonType = ButtonType;
  public readonly ePillType = PillType;
  public readonly clientId = 1;
  public readonly redirectUri = "";

  state: PageState;
  breadcrumb: Breadcrumb;

  integrationSetupForm: FormGroup;
  currentToken: ZendeskQAToken | null;

  zendeskQAError: string | null;

  get controlWorkspaceId(): FormControl {
    return this.integrationSetupForm.controls.workspaceId as FormControl;
  }

  get controlAPIKey(): FormControl {
    return this.integrationSetupForm.controls.APIKey as FormControl;
  }

  constructor(
    public route: ActivatedRoute,
    private breadcrumbService: BreadcrumbService,
    private zendeskQAAPIService: ZendeskQAAPIService,
    private swalUtils: SwalUtils,
    private notifyUtils: NotifyUtils,
  ) {
    this.currentToken = undefined;

    this.state = {
      loading: true,
      error: false,
      errorMessage: "",
      isConfiguring: false,
      submitted: false,
      submitting: false,
    };

    this.breadcrumb = this.breadcrumbService.init(this.route);
    this.integrationSetupForm = this.initFormSetup();

    this.zendeskQAError = localStorage.getItem("zendeskqa_error");
  }

  ngOnInit(): void {
    this.getData();
  }

  ngOnDestroy(): void {
    this.breadcrumbService.remove(this.breadcrumb);
  }

  getData(): void {
    console.log("Getting data");
    // TODO: Figure out what this function does and reimplement it for Zendesk QA if necessary
    this.checkForZendeskQAError();
    this.zendeskQAAPIService
      .getToken()
      .toPromise()
      .then((token) => {
        console.log("Got token", token);
        this.populatePageFromToken(token);
      })
      .finally(() => {
        this.state.loading = false;
      });
  }

  initFormSetup(token?: ZendeskQAToken): FormGroup {
    const formGroup = new FormGroup({
      workspaceId: new FormControl("", [
        Validators.required,
        Validators.maxLength(500),
        Validators.minLength(5),
        Validators.pattern(/^[0-9_]+$/),
      ]),
      APIKey: new FormControl("", [Validators.required, Validators.maxLength(500), Validators.minLength(5)]), // API keys are 49 characters long (at the time of writing)
    });

    if (token) {
      formGroup.controls.workspaceId.setValue(token.workspaceId);
      formGroup.controls.APIKey.setValue(token.accessToken);
    }

    return formGroup;
  }

  saveCredentials(): void {
    this.state.submitted = true;

    if (this.state.submitting) {
      return console.error("Form is already submitting");
    }
    if (this.integrationSetupForm.invalid) {
      return console.error("Form is invalid");
    }
    this.state.submitting = true;

    const updateRequest = {
      companyId: this.currentToken?.companyId,
      workspaceId: this.controlWorkspaceId.value.trim(),
      accessToken: this.controlAPIKey.value.trim(),
    } as ZendeskQAToken;

    this.zendeskQAAPIService
      .createToken(updateRequest)
      .toPromise()
      .then((token) => {
        this.populatePageFromToken(token);
        this.state.submitted = false;
        this.state.isConfiguring = false;
      })
      .finally(() => {
        this.state.submitting = false;
      });
  }

  populatePageFromToken(token: ZendeskQAToken): void {
    this.integrationSetupForm = this.initFormSetup(token);
    this.currentToken = token;
  }

  startSetup(): void {
    this.state.isConfiguring = true;
  }

  cancelSetup(): void {
    this.state.isConfiguring = false;
  }

  removeToken(): void {
    if (this.state.submitting) {
      return;
    }
    this.state.submitting = true;

    this.zendeskQAAPIService
      .removeToken()
      .toPromise()
      .then(() => {
        this.getData();
      })
      .finally(() => {
        this.state.submitting = false;
      });
  }

  checkForZendeskQAError() {
    if (this.zendeskQAError) {
      const timestamp = +this.zendeskQAError.split(":")[0];
      const message = this.zendeskQAError.split(":")[1];
      // Check if error is 30 seconds old or less
      if (Date.now() - timestamp < 30 * 1000) {
        this.swalUtils.displayErrorSwal({
          title: "Error",
          text: message,
          confirmButtonColor: "#2DA1B3",
        });
      }
      localStorage.removeItem("zendesk_error");
    }
  }

  runManualSync(): void {
    if (!this.currentToken) {
      return;
    }
    if (this.state.submitting) {
      return;
    }
    this.state.submitting = true;

    this.zendeskQAAPIService
      .runManualSync()
      .toPromise()
      .then(() => {
        // this.currentToken.syncing = true;
        this.notifyUtils.notify("Manual sync started");
        // After 2 minutes, refresh the page
        setTimeout(() => {
          this.state.submitting = false;
          this.state.loading = true;
          this.getData();
        }, 120000);
      });
  }
}
